"use client";

import { TrendingUp, DollarSign, FileText, Users, Target, Layers, Info} from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";
import { PremiumPiePanel, type PremiumSlice } from "./PremiumPiePanel";
import { UnderwritingPipelinePanel } from "./UnderwritingPipelinePanel";
import { SubAgencyScorecardPanel } from "./SubAgencyScorecardPanel";
import { AgencyAgentsPanel } from "./AgencyAgentsPanel";
import type { AgencyUserWithAgency } from "../../types/types";

const money = (n: number) => `$${Math.round(n || 0).toLocaleString()}`;

/**
 * The "View" deep-dive that opens beneath an agency row. Rolls up across every
 * level of the subtree:
 *   • a KPI strip (premium / policies / agents / closing ratio / avg policy)
 *   • premium distribution across sub-agencies (PremiumPiePanel)
 *   • the agents behind it (AgencyAgentsPanel — per-agent production and talk time)
 */
export function AgencyDeepDivePanel({
  agencyId,
  agencyName,
  period = "6-months",
  startDate,
  endDate,
  ownPremium = 0,
  slices,
  totalPremium,
  issuedPremium = 0,
  ownIssuedPremium = 0,
  totalPolicies,
  totalAgents,
  closingRatio,
  averagePolicySize,
  onViewAgentDetails,
}: {
  agencyId: string;
  agencyName: string;
  period?: "6-months" | "12-months" | "year-to-date";
  startDate?: string;
  endDate?: string;
  ownPremium?: number;
  slices: PremiumSlice[];
  totalPremium: number;
  totalPolicies: number;
  totalAgents: number;
  closingRatio: number;
  averagePolicySize: number;
  /** Sold & Approved only; totalPremium is gross and includes Underwriting. */
  issuedPremium?: number;
  /** This agency's OWN issued production (excludes descendants). */
  ownIssuedPremium?: number;
  /** Opens an agent's performance page from the Agents panel. */
  onViewAgentDetails?: (agencyUser: AgencyUserWithAgency) => void;
}) {
  const kpis: Array<{ label: string; value: string; Icon: typeof DollarSign; tint: string; hint?: string }> = [
    {
      label: "Total production",
      value: money(totalPremium),
      Icon: DollarSign,
      tint: "text-emerald-600 bg-emerald-50",
      hint: "This is your agency total reported production. Everything marked sold and approved as well as in underwriting.",
    },
    {
      label: "Total issued",
      value: money(issuedPremium),
      Icon: DollarSign,
      tint: "text-teal-600 bg-teal-50",
      hint: "This is your agency total reported net production. Everything marked with sold and approved strictly shows up here.",
    },
    { label: "Policies", value: (totalPolicies || 0).toLocaleString(), Icon: FileText, tint: "text-blue-600 bg-blue-50" },
    { label: "Agents", value: (totalAgents || 0).toLocaleString(), Icon: Users, tint: "text-violet-600 bg-violet-50" },
    { label: "Closing ratio", value: `${(closingRatio || 0).toFixed(1)}%`, Icon: Target, tint: "text-amber-600 bg-amber-50" },
    { label: "Avg policy", value: money(averagePolicySize), Icon: TrendingUp, tint: "text-rose-600 bg-rose-50" },
  ];

  return (
    <div className="space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
        <Layers className="h-4 w-4 text-primary" />
        {agencyName} — deep dive
        <span className="font-normal text-muted-foreground">· combined across all levels</span>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {kpis.map(({ label, value, Icon, tint, hint }) => (
          <div key={label} className="rounded-lg border border-gray-200 bg-white p-3">
            <div className="flex items-center gap-2">
              <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md ${tint}`}>
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-1 truncate text-[11px] uppercase tracking-wide text-muted-foreground">
                  <span className="truncate">{label}</span>
                  {!!hint && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" aria-label="Info" className="inline-flex shrink-0 cursor-help items-center">
                            <Info className="h-3 w-3 text-primary/70 transition-colors hover:text-primary" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-64 text-xs normal-case">
                          {hint}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <p className="truncate text-sm font-semibold tabular-nums text-gray-900">{value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Premium by sub-agency + the agents producing it */}
      {/* items-start: size each card to its own content. Stretching them to match
          made the shorter one centre inside a tall cell with big voids. */}
      <div className="grid items-start gap-3 lg:grid-cols-2">
        {/* Left column stacks three panels so it matches the agents card's
            height instead of leaving a tall gap beside it. */}
        <div className="space-y-3">
          <PremiumPiePanel
            agencyName={agencyName}
            ownPremium={ownPremium}
            ownIssuedPremium={ownIssuedPremium}
            slices={slices}
          />
          <UnderwritingPipelinePanel
            agencyName={agencyName}
            ownPremium={ownPremium}
            ownIssuedPremium={ownIssuedPremium}
            slices={slices}
          />
          <SubAgencyScorecardPanel slices={slices} />
        </div>
        <AgencyAgentsPanel
          agencyId={agencyId}
          period={period}
          startDate={startDate}
          endDate={endDate}
          onViewAgentDetails={onViewAgentDetails}
        />
      </div>
    </div>
  );
}
